import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CartContext = createContext(null);

export const effPrice = (p) =>
  p && p.sale_price_cents && p.sale_price_cents < p.price_cents ? p.sale_price_cents : p ? p.price_cents : 0;

export function CartProvider({ children }) {
  const [products, setProducts] = useState([]);
  const [content, setContent] = useState(null);
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("vf_cart") || "[]");
    } catch {
      return [];
    }
  });
  const [orderNote, setOrderNote] = useState(() => localStorage.getItem("vf_order_note") || "");
  const [drawerOpen, setDrawerOpen] = useState(false);

  const refreshProducts = useCallback(() => {
    axios.get(`${API}/products`).then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const refreshContent = useCallback(() => {
    axios.get(`${API}/content`).then((r) => setContent(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    refreshContent();
  }, [refreshContent]);

  // Content lookup with dot path: ct("hero.headline", "fallback")
  const ct = useCallback(
    (path, fallback) => {
      if (!content) return fallback;
      const val = path.split(".").reduce((o, k) => (o == null ? o : o[k]), content);
      return val === undefined || val === null || val === "" ? fallback : val;
    },
    [content]
  );

  useEffect(() => {
    refreshProducts();
  }, [refreshProducts]);

  // Refetch catalog when the drawer opens or the tab regains focus, so stock stays fresh
  useEffect(() => {
    if (drawerOpen) refreshProducts();
  }, [drawerOpen, refreshProducts]);

  useEffect(() => {
    const onFocus = () => {
      refreshProducts();
      refreshContent();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [refreshProducts, refreshContent]);

  // Prune cart items that no longer exist in the catalog (e.g. deleted products)
  useEffect(() => {
    if (products.length === 0) return;
    setItems((prev) => {
      const valid = prev.filter((i) => products.some((p) => p.product_id === i.product_id));
      return valid.length === prev.length ? prev : valid;
    });
  }, [products]);

  useEffect(() => {
    localStorage.setItem("vf_cart", JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    localStorage.setItem("vf_order_note", orderNote);
  }, [orderNote]);

  const addItem = useCallback((productId, quantity = 1) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.product_id === productId);
      if (existing) {
        return prev.map((i) =>
          i.product_id === productId ? { ...i, quantity: Math.min(20, i.quantity + quantity) } : i
        );
      }
      return [...prev, { product_id: productId, quantity }];
    });
  }, []);

  const updateQty = useCallback((productId, quantity) => {
    setItems((prev) =>
      quantity <= 0
        ? prev.filter((i) => i.product_id !== productId)
        : prev.map((i) => (i.product_id === productId ? { ...i, quantity } : i))
    );
  }, []);

  const removeItem = useCallback((productId) => {
    setItems((prev) => prev.filter((i) => i.product_id !== productId));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const productMap = useMemo(() => {
    const m = {};
    products.forEach((p) => (m[p.product_id] = p));
    return m;
  }, [products]);

  const count = items.reduce((s, i) => s + i.quantity, 0);
  const subtotal = items.reduce((s, i) => {
    const p = productMap[i.product_id];
    return p ? s + effPrice(p) * i.quantity : s;
  }, 0);

  const value = {
    products,
    productMap,
    items,
    count,
    subtotal,
    orderNote,
    setOrderNote,
    refreshProducts,
    ct,
    addItem,
    updateQty,
    removeItem,
    clearCart,
    drawerOpen,
    setDrawerOpen,
  };
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  return useContext(CartContext);
}
